import { useEffect, useState } from "react";
import {
  getRallyEvent,
  saveRallyEvent,
  downloadRallyCsv,
  type RallyEventInfo,
} from "../lib/contentApi";
import { ActionButton } from "./ActionButton";
import { useRegisterSave } from "../lib/saveBar";
import { normalizeFormUrl } from "../lib/formUrl";
import { Msg } from "./ui";
import { isoFromKstDateTime, kstDateTimeInput, kstShort } from "@greg-chick/shared"; // 🕘 한국 시각 — 기기 시간대와 상관없이(kstTime.ts · 2026-10-16)

/**
 * 랠리 이벤트 운영 패널(FR-RALLY) — 개발자 없이 운영자가:
 *  ① 선착순 선물 인원 ② 행사 기간 ③ 하루 운영시간 ④ 상품 신청 폼 URL 을 정하고
 *  ⑤ 완주자 현황을 보고 CSV(개인정보 없음: 순위·완주코드)를 내려받는다.
 * 저장은 정적 기본값 위에 덮어쓰는 오버라이드 — 비워두면 기본값이 쓰인다.
 */
export function RallyEventPanel() {
  const [info, setInfo] = useState<RallyEventInfo | null>(null);
  const [winners, setWinners] = useState("");
  const [start, setStart] = useState(""); // datetime-local ("YYYY-MM-DDTHH:mm")
  const [end, setEnd] = useState("");
  const [open, setOpen] = useState("");
  const [close, setClose] = useState("");
  const [formUrl, setFormUrl] = useState("");
  const [msg, setMsg] = useState<string | null>(null);

  async function refresh() {
    const r = await getRallyEvent();
    setInfo(r);
    // 편집칸엔 '유효 병합본'을 채운다 — 운영자는 지금 적용값을 그대로 보고 고친다.
    setWinners(String(r.event.winners));
    setStart(toLocalInput(r.event.start));
    setEnd(toLocalInput(r.event.end));
    setOpen(r.event.hours?.open ?? "");
    setClose(r.event.hours?.close ?? "");
    setFormUrl(r.event.claimFormUrl ?? "");
  }
  useEffect(() => {
    refresh().catch(() => setMsg("엣지 서버에 연결하지 못했어요. 서버가 떠 있는지 확인해 주세요."));
  }, []);

  async function onSave() {
    const doc: Parameters<typeof saveRallyEvent>[0] = {};
    const n = Number(winners);
    if (Number.isInteger(n) && n >= 1) doc.winners = n;
    if (start) doc.start = fromLocalInput(start);
    if (end) doc.end = fromLocalInput(end);
    if (open && close) doc.hours = { open, close };
    // 「forms.gle/…」·「http://」 같은 흔한 모양은 고쳐 보낸다(lib/formUrl)
    const wantForm = normalizeFormUrl(formUrl);
    if (wantForm) doc.claimFormUrl = wantForm;
    const saved = await saveRallyEvent(doc);
    /**
     * 🔴 **서버가 신청 폼 주소를 안 받았으면 성공이라고 하지 않는다**(2026-10-21).
     * 엣지는 https 가 아니거나 자리표시자인 주소를 조용히 버린다 — 예전엔 그래도 「✅ 저장했어요」였다.
     * 다른 값(인원·기간)은 저장됐으니 칸은 새로 읽고, 저장 바에는 실패로 알린다.
     */
    if (wantForm && saved.claimFormUrl !== wantForm) {
      await refresh();
      setFormUrl(wantForm); // 넣은 것은 남겨 둔다 — 고쳐서 다시 올리게
      const say = `⚠️ 신청 폼 주소가 저장되지 않았어요 — 「https://」로 시작하는 진짜 폼 주소인지 확인해 주세요(예: https://forms.gle/…). 인원·기간은 저장됐어요.`;
      setMsg(say);
      throw new Error(say);
    }
    setMsg(
      `✅ 저장했어요 — 선착순 ${saved.winners}명 · ` +
        `${fmt(saved.start)} ~ ${fmt(saved.end)}` +
        (saved.hours ? ` · 매일 ${saved.hours.open}~${saved.hours.close}` : "") +
        ` · 신청 폼 ${saved.claimFormUrl ? "걸림" : "없음(완주한 아이에게 신청 단추가 안 떠요)"}` +
        `. 웹 랠리 화면에 바로 반영돼요.`,
    );
    await refresh();
  }
  useRegisterSave({
    id: "rallyev",
    label: "랠리 선물",
    save: onSave,
    draft: {
      data: () => ({ winners, start, end, open, close, formUrl }),
      restore: (d) => {
        const v = d as Record<string, string | undefined>;
        if (typeof v.winners === "string") setWinners(v.winners);
        if (typeof v.start === "string") setStart(v.start);
        if (typeof v.end === "string") setEnd(v.end);
        if (typeof v.open === "string") setOpen(v.open);
        if (typeof v.close === "string") setClose(v.close);
        if (typeof v.formUrl === "string") setFormUrl(v.formUrl);
      },
    },
  });

  return (
    <section className="panel rallyev">
      <header className="panel__head">
        <h2>🎁 랠리 이벤트</h2>
        <p>
          완주(스탬프 5개) 선착순 선물 이벤트를 운영해요. 개인정보는 서버에 없어요 —
          당첨자는 <b>완주 코드</b>로 외부 폼에서 신청해요.
        </p>
      </header>

      {info?.stats ? (
        <div className="rallyev__stats" role="status">
          <span>🧒 스탬프 찍은 아이 <b>{info.stats.people ?? 0}</b>명</span>
          <span>🏁 완주 <b>{info.stats.completions}</b>명</span>
          <span>🎁 선물 지급 <b>{info.stats.awarded}</b>명</span>
          <span>🎯 선착순 <b>{info.event.winners}</b>명</span>
          <ActionButton onAction={downloadRallyCsv} doneLabel="받았어요" className="btn btn--sm">
            완주자 명단 CSV
          </ActionButton>
        </div>
      ) : null}

      {/*
        🔴 **어느 지점에서 끊기는지.** 랠리는 경주 현장에 **종이 QR 을 붙여야** 돌아간다.
        한 지점이 막혀 있으면 아이들은 거기서 포기하고, 완주는 0 이 된다 —
        운영자는 「아무도 관심이 없다」고 읽는다. 지점별로 보면 현장에 다시 가지 않고도
        어디가 문제인지 안다. 0 인 지점도 **일부러 보여 준다**(그게 신호다).
      */}
      {info?.stats && info.stats.stamps > 0 ? (
        <div className="rallyev__points">
          <h3>지점별 스탬프 — 어디서 끊기나</h3>
          <ul className="rallyev__pointlist">
            {(info.points ?? []).map((p) => {
              const n = info.stats?.byPoint.find((b) => b.pointId === p.id)?.n ?? 0;
              return (
                <li key={p.id} className={n === 0 ? "is-zero" : undefined}>
                  <span>{p.title_ko || p.id}</span>
                  <b>{n}</b>
                </li>
              );
            })}
          </ul>
          <small>
            0 인 지점이 있으면 그 자리의 종이 QR 을 확인해 주세요 — 떨어졌거나 가려졌을 수 있어요.
          </small>
        </div>
      ) : null}

      <div className="rallyev__grid">
        <label>
          선착순 선물 인원
          <input
            type="number"
            min={1}
            value={winners}
            onChange={(e) => setWinners(e.target.value)}
            placeholder="예: 50"
          />
          <small>완주 순서가 이 인원 안이면 "선착순 상품 대상!"으로 표시돼요.</small>
        </label>
        <label>
          행사 시작
          <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} />
        </label>
        <label>
          행사 종료
          <input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} />
        </label>
        <label>
          하루 운영시간
          <span className="rallyev__hours">
            <input type="time" value={open} onChange={(e) => setOpen(e.target.value)} />
            <span>~</span>
            <input type="time" value={close} onChange={(e) => setClose(e.target.value)} />
          </span>
          <small>운영시간 밖에는 스탬프 인증이 안 돼요(안전한 시간대만).</small>
        </label>
        <label className="rallyev__wide">
          상품 신청 폼 주소(https)
          <input
            type="url"
            value={formUrl}
            onChange={(e) => setFormUrl(e.target.value)}
            placeholder="https://forms.gle/… (구글/네이버 폼)"
          />
          <small>
            보호자가 이 폼에 <b>완주 코드 + 배송 정보</b>를 적어요. 개인정보는 폼(운영자 계정)에만
            남고 우리 서버엔 저장되지 않아요. 폼 항목에 "완주 코드"를 꼭 넣어 주세요.
          </small>
        </label>
      </div>

      <div className="rallyev__actions">
      </div>

      <Msg text={msg} />

      <details className="rallyev__help">
        <summary>운영 순서(선물 지급 방법)</summary>
        <ol>
          <li>행사 전: 위에서 인원·기간·폼 주소를 정하고 저장해요.</li>
          <li>완주자는 화면에서 <b>완주 코드</b>를 받고, 폼에 코드+배송정보를 적어요.</li>
          <li>운영자는 <b>완주자 명단 CSV</b>를 받아 폼 응답의 코드와 대조해요(코드 1개=선물 1개).</li>
          <li>순위가 선착순 인원 안이면 발송! (동일 코드 중복 응답은 첫 응답만 인정)</li>
        </ol>
      </details>
    </section>
  );
}

/** ISO(+09:00 포함) → datetime-local 입력값. 파싱 불가면 빈 값. */
function toLocalInput(iso?: string): string {
  return kstDateTimeInput(iso); // 🔴 기기 시간대로 풀면 다른 시간대 PC 에서 랠리 기간이 저장마다 밀렸다
}
/** datetime-local → KST 명시 ISO (행사는 한국에서 열린다 — 서버·기기 시간대 차이로 어긋나지 않게). */
function fromLocalInput(v: string): string {
  return isoFromKstDateTime(v) ?? `${v}:00+09:00`;
}
function fmt(iso?: string): string {
  return kstShort(iso);
}
